import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Textarea } from '@/components/atoms/textarea';
import {
  SELLER_REFUND_REASONS,
  buildSellerRefundPayload,
  type SellerRefundPayload,
} from '../refund/seller-refund-reasons';

export interface SellerRefundModalProps {
  open: boolean;
  orderId: string;
  orderLabel: string;
  maxRefundAmount: number;
  currency: string;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: (payload: SellerRefundPayload) => void;
}

export function SellerRefundModal({
  open,
  orderLabel,
  maxRefundAmount,
  currency,
  isLoading = false,
  onClose,
  onConfirm,
}: SellerRefundModalProps) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState<string>(String(maxRefundAmount));
  const [reasonKey, setReasonKey] = useState<string>('');
  const [comment, setComment] = useState('');

  const numericAmount = Number(amount);
  const amountValid = Number.isFinite(numericAmount) && numericAmount > 0 && numericAmount <= maxRefundAmount;
  const canConfirm = !!reasonKey && amountValid && !isLoading;

  const handleConfirm = () => {
    const picked = SELLER_REFUND_REASONS.find((r) => r.key === reasonKey);
    if (!picked || !amountValid) return;
    onConfirm(
      buildSellerRefundPayload({
        amount: numericAmount,
        reasonKey: picked.key,
        reasonLabel: t(picked.labelKey, picked.defaultLabel),
        comment,
      }),
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('orders.refund.title', 'Rembourser la commande')} {orderLabel}
          </DialogTitle>
          <DialogDescription>
            {t('orders.refund.subtitle', 'Pourquoi souhaitez-vous rembourser cette commande ?')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="seller-refund-amount">
              {t('orders.refund.amountLabel', 'Montant à rembourser')} ({currency})
            </Label>
            <Input
              id="seller-refund-amount"
              type="number"
              min={0.01}
              max={maxRefundAmount}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              {t('orders.refund.max', 'Maximum')}&nbsp;: {maxRefundAmount} {currency}
            </p>
          </div>

          <fieldset className="space-y-1.5">
            <legend className="mb-1 text-sm font-medium">
              {t('orders.refund.reasonLabel', 'Motif du remboursement')}
            </legend>
            {SELLER_REFUND_REASONS.map((reason) => {
              const Icon = reason.icon;
              return (
                <label
                  key={reason.key}
                  className="flex items-center gap-3 rounded-md border p-2.5 text-sm"
                >
                  <input
                    type="radio"
                    name="seller-refund-reason"
                    value={reason.key}
                    checked={reasonKey === reason.key}
                    onChange={() => setReasonKey(reason.key)}
                  />
                  <Icon className="text-muted-foreground h-4 w-4" aria-hidden="true" />
                  <span>{t(reason.labelKey, reason.defaultLabel)}</span>
                </label>
              );
            })}
          </fieldset>

          <div className="space-y-1.5">
            <Label htmlFor="seller-refund-comment">
              {t('orders.refund.commentLabel', 'Commentaire (optionnel)')}
            </Label>
            <Textarea
              id="seller-refund-comment"
              rows={2}
              maxLength={500}
              placeholder={t('orders.refund.commentPlaceholder', 'Précisez le contexte…')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            {t('orders.refund.confirm', 'Confirmer le remboursement')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
